import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Star, FileText, X } from "lucide-react";
import {
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Box,
  Button,
  IconButton,
  useTheme,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  Chip,
  CircularProgress,
  Pagination,
  Alert,
  Snackbar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import PDFViewer from "./PDFViewer";
import { useTranslation } from "react-i18next";

const StyledCard = styled(Card)(({ theme }) => ({
  transition: "transform 0.2s",
  backgroundColor: "#ffffff",
  border: "1px solid rgba(0, 23, 231, 0.1)",
  borderRadius: "12px",
  background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
  "&:hover": {
    transform: "translateY(-4px)",
    borderColor: "rgba(0, 23, 231, 0.2)",
    boxShadow:
      "0 10px 15px -3px rgba(0, 105, 255, 0.1), 0 4px 6px -2px rgba(0, 105, 255, 0.05)",
  },
}));

const ViewResultCarnival = () => {
  const { carnivalId } = useParams();
  const { t } = useTranslation();
  const [interviews, setInterviews] = useState({
    all: [],
    starred: [],
  });
  const [pagination, setPagination] = useState({
    all: { page: 1, totalPages: 1 },
    starred: { page: 1, totalPages: 1 },
  });
  const [loading, setLoading] = useState({
    main: true,
    cv: {},
    notes: {},
  });
  const [activeCV, setActiveCV] = useState(null);
  const [activeNotes, setActiveNotes] = useState(null);
  const [tabValue, setTabValue] = useState("all");
  const [error, setError] = useState(null);
  const theme = useTheme();
  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchData = async () => {
    try {
      setLoading((prev) => ({ ...prev, main: true }));
      const token = localStorage.getItem("accessToken");
      const currentTab = tabValue;
      const currentPage = pagination[currentTab].page;

      const params = {
        carnival_id: carnivalId,
        status: "completed",
        page: currentPage,
        ...(currentTab === "starred" && { is_starred: true }),
      };

      const response = await axios.get(
        `${apiUrl}opportunities/api/interview-rooms/`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Handle the array response
      const interviewsArray = Array.isArray(response.data) ? response.data : [];
      console.log("Received interviews:", interviewsArray);

      setInterviews((prev) => ({
        ...prev,
        [currentTab]: interviewsArray,
      }));

      // Since we don't have a count from the API, we'll set totalPages to 1
      setPagination((prev) => ({
        ...prev,
        [currentTab]: {
          page: 1,
          totalPages: 1, // Set to 1 since we're getting all data at once
        },
      }));
    } catch (err) {
      console.error("Error in fetchData:", err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading((prev) => ({ ...prev, main: false }));
    }
  };

  const toggleStar = async (roomId) => {
    try {
      // First update the UI immediately for better UX
      setInterviews((prev) => {
        // Update the 'all' tab
        const updatedAll = prev.all.map((ir) =>
          ir.id === roomId ? { ...ir, is_starred: !ir.is_starred } : ir
        );

        // Find the interview that was toggled
        const toggledInterview = updatedAll.find((ir) => ir.id === roomId);

        // Update the 'starred' tab based on the new starred state
        let updatedStarred = [...prev.starred];
        if (toggledInterview.is_starred) {
          // If it's newly starred, add it to starred list
          if (!updatedStarred.some((ir) => ir.id === roomId)) {
            updatedStarred = [...updatedStarred, toggledInterview];
          }
        } else {
          // If it's newly unstarred, remove it from starred list
          updatedStarred = updatedStarred.filter((ir) => ir.id !== roomId);
        }

        return {
          all: updatedAll,
          starred: updatedStarred,
        };
      });

      // Then make the API call
      const token = localStorage.getItem("accessToken");
      await axios.put(
        `${apiUrl}opportunities/api/interview-rooms/${roomId}/toggle-star/`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Fetch updated data in the background to sync with server
      fetchData();
    } catch (err) {
      // If the API call fails, revert the UI changes
      setError(err.message);
      // Re-fetch to restore the correct state
      fetchData();
    }
  };

  const fetchCV = async (roomId) => {
    try {
      setLoading((prev) => ({
        ...prev,
        cv: { ...prev.cv, [roomId]: true },
      }));
      const token = localStorage.getItem("accessToken");
      const response = await axios.get(
        `${apiUrl}opportunities/api/candidates/${roomId}/cv/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setActiveCV(response.data.pages);
    } catch (err) {
      setError(t("carnivals.error"));
    } finally {
      setLoading((prev) => ({
        ...prev,
        cv: { ...prev.cv, [roomId]: false },
      }));
    }
  };

  const fetchNotes = async (roomId) => {
    try {
      setLoading((prev) => ({
        ...prev,
        notes: { ...prev.notes, [roomId]: true },
      }));
      const token = localStorage.getItem("accessToken");
      const response = await axios.get(
        `${apiUrl}opportunities/api/interview-rooms/${roomId}/notes/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.data) {
        throw new Error("Failed to load notes");
      }

      setActiveNotes(response.data);
    } catch (err) {
      setError(t("carnivals.error"));
    } finally {
      setLoading((prev) => ({
        ...prev,
        notes: { ...prev.notes, [roomId]: false },
      }));
    }
  };

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      [tabValue]: { ...prev[tabValue], page: 1 },
    }));
  }, [tabValue]);

  useEffect(() => {
    if (carnivalId) {
      fetchData();
    }
  }, [carnivalId, tabValue, pagination[tabValue].page]);

  const currentInterviews = interviews[tabValue] || [];
  const currentPagination = pagination[tabValue];

  if (loading.main) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress size={60} thickness={4} sx={{ color: "#0017E7" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto", bgcolor: "#f8fafc" }}>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>

      <Tabs
        value={tabValue}
        onChange={(e, v) => setTabValue(v)}
        sx={{
          mb: 3,
          "& .MuiTabs-indicator": {
            background: "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
          },
          "& .MuiTab-root.Mui-selected": {
            color: "#0017E7",
            fontWeight: 600,
          },
        }}
      >
        <Tab label={t("carnivals.allInterviews")} value="all" />
        <Tab
          label={t("carnivals.starredCandidates")}
          value="starred"
          icon={
            <Star
              size={18}
              fill={tabValue === "starred" ? "#0069ff" : "transparent"}
            />
          }
          iconPosition="start"
        />
      </Tabs>

      <Grid container spacing={3}>
        {currentInterviews.map((interview) => (
          <Grid item xs={12} md={6} key={interview.id}>
            <StyledCard>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6" fontWeight="600" color="#0017E7">
                    {interview.candidate_name}
                  </Typography>
                  <IconButton onClick={() => toggleStar(interview.id)}>
                    <Star
                      size={24}
                      style={{
                        fill: interview.is_starred ? "#0017E7" : "transparent",
                        color: interview.is_starred ? "#0017E7" : "#94a3b8",
                      }}
                    />
                  </IconButton>
                </Box>

                <Box mt={2} display="flex" gap={2} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    startIcon={<FileText size={18} />}
                    onClick={() => fetchCV(interview.id)}
                    disabled={loading.cv[interview.id]}
                    sx={{
                      borderColor: "#0017E7",
                      color: "#0017E7",
                      "&:hover": {
                        borderColor: "#0010a3",
                        backgroundColor: "rgba(0, 23, 231, 0.04)",
                      },
                    }}
                  >
                    {loading.cv[interview.id] ? t("carnivals.loading") : t("carnivals.viewCv")}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => fetchNotes(interview.id)}
                    disabled={loading.notes[interview.id]}
                    sx={{
                      borderColor: "#0017E7",
                      color: "#0017E7",
                      "&:hover": {
                        borderColor: "#0010a3",
                        backgroundColor: "rgba(0, 23, 231, 0.04)",
                      },
                    }}
                  >
                    {loading.notes[interview.id] ? t("carnivals.loading") : t("carnivals.viewNotes")}
                  </Button>
                  <Chip
                    label={`${new Date(
                      interview.created_at
                    ).toLocaleDateString()}`}
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: "#94a3b8",
                      color: "#475569",
                    }}
                  />
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      {currentInterviews.length > 0 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={currentPagination.totalPages}
            page={currentPagination.page}
            onChange={(e, page) =>
              setPagination((prev) => ({
                ...prev,
                [tabValue]: { ...prev[tabValue], page },
              }))
            }
            color="primary"
            shape="rounded"
            sx={{
              "& .MuiPaginationItem-root": {
                color: "#475569",
              },
              "& .MuiPaginationItem-root.Mui-selected": {
                background: "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
                color: "white",
              },
            }}
          />
        </Box>
      )}

      <Dialog
        open={!!activeCV}
        onClose={() => setActiveCV(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: "#f8fafc", color: "#0017E7" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {t("carnivals.candidateCv")}
            <IconButton onClick={() => setActiveCV(null)}>
              <X size={24} color="#475569" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: "100%", p: 0 }}>
          <PDFViewer urls={activeCV} />
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!activeNotes}
        onClose={() => setActiveNotes(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: "#f8fafc", color: "#0017E7" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {t("carnivals.interviewNotes")}
            <IconButton onClick={() => setActiveNotes(null)}>
              <X size={24} color="#475569" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: "#ffffff" }}>
          {activeNotes && activeNotes.length > 0 ? (
            activeNotes.map((note) => (
              <Box
                key={note.id}
                sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: "#f8fafc",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "rgba(0, 23, 231, 0.1)",
                }}
              >
                <Typography
                  variant="body1"
                  whiteSpace="pre-wrap"
                  color="#1e293b"
                >
                  {note.content}
                </Typography>
                <Typography
                  variant="caption"
                  color="#64748b"
                  mt={1}
                  display="block"
                >
                  {new Date(note.created_at).toLocaleString()}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography color="#64748b" align="center" py={3}>
              {t("carnivals.noNotesAvailable")}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ViewResultCarnival;
